import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PhoneCall, UserPlus, CalendarCheck, AlertCircle, Sparkles,
  ChevronRight, Loader2, RefreshCw
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { API } from "../App";

const TYPE_CONFIG = {
  never_contacted: {
    icon: UserPlus,
    color: "text-rose-600",
    bg: "bg-rose-50",
    tag: "New Lead",
    tagColor: "bg-rose-100 text-rose-700",
  },
  unassigned_lead: {
    icon: UserPlus,
    color: "text-orange-600",
    bg: "bg-orange-50",
    tag: "Unassigned",
    tagColor: "bg-orange-100 text-orange-700",
  },
  escalation: {
    icon: AlertCircle,
    color: "text-red-600",
    bg: "bg-red-50",
    tag: "Escalation",
    tagColor: "bg-red-100 text-red-700",
  },
  stale_lead: {
    icon: PhoneCall,
    color: "text-amber-600",
    bg: "bg-amber-50",
    tag: "Needs Follow-up",
    tagColor: "bg-amber-100 text-amber-700",
  },
  upcoming_visit: {
    icon: CalendarCheck,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    tag: "Visit",
    tagColor: "bg-indigo-100 text-indigo-700",
  },
  no_task_hot_lead: {
    icon: AlertCircle,
    color: "text-red-600",
    bg: "bg-red-50",
    tag: "No Task",
    tagColor: "bg-red-100 text-red-700",
  },
  positive_notes_stale: {
    icon: Sparkles,
    color: "text-violet-600",
    bg: "bg-violet-50",
    tag: "Re-engage",
    tagColor: "bg-violet-100 text-violet-700",
  },
};

const SCORE_STYLES = {
  hot: "bg-red-100 text-red-700",
  warm: "bg-amber-100 text-amber-700",
  nurture: "bg-blue-100 text-blue-700",
  cold: "bg-sky-100 text-sky-600",
  dormant: "bg-slate-100 text-slate-500",
  converted: "bg-green-100 text-green-700",
};

function timeAgo(isoStr) {
  if (!isoStr) return "";
  try {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return "";
  }
}

export const RecommendedTodos = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [error, setError] = useState(null);

  const fetchTodos = useCallback(async () => {
    setError(null);
    try {
      console.log("Fetching todos from:", `${API}/dashboard/recommended-todos`);
      const res = await axios.get(`${API}/dashboard/recommended-todos`);
      console.log("Todos response:", res.data);
      setItems(res.data.items || []);
      setGeneratedAt(res.data.generated_at || null);
    } catch (err) {
      console.error("Failed to fetch todos:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      if (err.response?.status === 401) {
        setError("Session expired - please refresh the page");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError("Failed to load - check your connection");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/dashboard/recommended-todos/refresh`);
      setItems(res.data.items || []);
      setGeneratedAt(res.data.generated_at || null);
    } catch (err) {
      console.error("Failed to refresh todos:", err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="recommended-todos-loading">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading your to-do list...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="recommended-todos-error">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2.5 text-xs"
            onClick={fetchTodos}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="recommended-todos-empty">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-500">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm">No pending actions - you're all caught up!</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2.5 text-xs"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="recommended-todos">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Recommended Actions</h3>
            <p className="text-xs text-slate-500">{items.length} items need your attention</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {generatedAt && (
            <span className="text-[11px] text-slate-400">Updated {timeAgo(generatedAt)}</span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2.5 text-xs"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-todos-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
          <Badge variant="outline" className="text-xs font-medium text-slate-500">
            AI Prioritized
          </Badge>
        </div>
      </div>

      <div className="max-h-[320px] overflow-y-auto divide-y divide-slate-50" data-testid="recommended-todos-list">
        {items.map((item, idx) => {
          const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.stale_lead;
          const Icon = cfg.icon;

          return (
            <div
              key={`${item.lead_id}-${idx}`}
              className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/80 transition-colors cursor-pointer group"
              onClick={() => navigate(`/leads/${item.lead_id}`)}
              data-testid={`todo-item-${item.lead_id}`}
            >
              <div className={`w-8 h-8 ${cfg.bg} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon className={`w-4 h-4 ${cfg.color}`} />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{item.title}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{item.subtitle}</p>
                {item.recommended_action && (
                  <p className="text-[11px] text-indigo-600 mt-0.5 truncate">{item.recommended_action}</p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {item.signals?.length > 0 && (
                  <div className="hidden md:flex items-center gap-1">
                    {item.signals.slice(0, 2).map((sig, si) => (
                      <span key={si} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{sig}</span>
                    ))}
                  </div>
                )}
                <Badge className={`text-[10px] px-1.5 py-0.5 ${cfg.tagColor} border-0`}>
                  {cfg.tag}
                </Badge>
                {item.score && (
                  <Badge className={`text-[10px] px-1.5 py-0.5 capitalize border-0 ${SCORE_STYLES[item.score] || ""}`}>
                    {item.score}
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2.5 text-xs text-slate-600 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/leads/${item.lead_id}`);
                  }}
                  data-testid={`todo-action-${item.lead_id}`}
                >
                  {item.action_label}
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
